import React, { useMemo, useCallback } from "react";
import {
  FormProvider,
  useForm,
  type SubmitHandler,
  type Resolver,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldRenderer } from "../../../components/form/FieldRenderer";
import { licenseCount } from "./config";
import {
  reduceMspProductLicenseFormSchema,
  ReduceMSPProductLicenseFormValues,
} from "./schema";
import { SECTION_CARD_CLASS } from "../../../constants/mspAndCustomerForm";
import { ReduceMspProductLicenseFormProps } from "./types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { reduceLicensesFromMSPSubscription } from "../services/mspService";
export default function ReduceMspProductLicenseForm({
  subscription,
  onProductLicenseReduced,
  onCancel,
}: ReduceMspProductLicenseFormProps) {
  const dispatch = useAppDispatch();
  const resolver = useMemo(
    () =>
      zodResolver(
        reduceMspProductLicenseFormSchema
      ) as Resolver<ReduceMSPProductLicenseFormValues>,
    []
  );
  const selectedMspId = useAppSelector(
    (state) => state.selectedMsp.selectedMspId
  );

  const DEFAULT_VALUES = {
    no_of_licenses: undefined,
  };

  const methods = useForm<ReduceMSPProductLicenseFormValues>({
    resolver,
    mode: "onBlur",
    shouldFocusError: false,
    shouldUnregister: false,
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = methods;

  // Form submission handler
  const onSubmit: SubmitHandler<ReduceMSPProductLicenseFormValues> = useCallback(
    async (formData) => {
      try {
        const payload = {
          licenses: formData.no_of_licenses || 1,
        };
        await reduceLicensesFromMSPSubscription(selectedMspId!, subscription?.id!, payload);
        reset();
        onCancel && onCancel();
        onProductLicenseReduced && onProductLicenseReduced();
      } catch (error) {
        console.error("Failed to reduce license:", error);
      }
    },
    [dispatch, reset]
  );

  // Render functions for organization
  const renderHeader = () => (
    <div className="mb-4 flex items-start justify-between p-2">
      <div>
        <h2 className="text-[26px] font-semibold text-[#4F4F4F]">
          Reduce Licenses
        </h2>
      </div>
    </div>
  );

  const renderFormSection = (title: React.ReactNode, config: any[]) => (
    <>
      <h3 className="text-[15px] font-bold text-[#2B2B2B]">{title}</h3>
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-1">
        {config.map((f) => (
          <FieldRenderer key={f.name} cfg={f} />
        ))}
      </div>
    </>
  );

  const reducibleLicenses = subscription?.no_of_licenses! - subscription?.used_licenses!;

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6"
          autoComplete="off"
        >
          {renderHeader()}

          <div className={SECTION_CARD_CLASS}>
            <div>
              {renderFormSection(
                <>
                  {subscription?.planPrice.plan.product.product_name}
                  <br />
                  <br />

                  <span className="block text-[13px] font-semibold text-[#3B3B3B]">
                    Total licenses: {subscription?.no_of_licenses}
                    <br />
                    Reducible licenses: {subscription?.used_licenses ? reducibleLicenses : reducibleLicenses - 1}
                  </span>
                  <br />
                  <span className="block text-[13px] font-semibold text-[#3B3B3B]">

                  </span>
                </>,
                licenseCount
              )}

            </div>

            <div className="mt-8 flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex min-w-[180px] items-center justify-center rounded-[6px] bg-[#1D58E2] px-8 py-3 text-[15px] font-semibold text-white shadow hover:brightness-110 disabled:opacity-50 transition-all duration-200"
              >
                {isSubmitting ? "REDUCING..." : "Confirm Reduction"}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
}
